import React, { createContext, useContext, useEffect, useState, memo, useRef, useMemo } from "react";
import { normalizarSearchableName } from '../utils/searchableName';
import { auth, secondaryAuth } from "../utils/init-firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  getAuth,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithCustomToken,
} from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import UsersService from "../services/Users.Service";
import { coalescerCadastro } from "../utils/coalescerCadastro";
import { garantirGrupoPadrao } from "../services/GrupoPadrao.service";
import { getUrlDetails } from "../utils/UrlDomainUtil";
import { GetPortal } from "../utils/urlPortalUtil";
import GenericService from "../services/Generic.service";
import GenericVinculadoService from "../services/GenericVinculado.service";
import UsersGroupsService from "../services/UsersGroups.service";
import { isCpf } from "validator-brazil";
import AuthenticatedApiService from "../services/AuthenticatedApiService";
import { definirPortalCrm } from "../services/crmPortal";
import { CBTKD_ID } from "../constantes/EntidadesConstantes";
import PraticantesService from "../services/Praticantes.Service";
import {
  esquecerSincronizacaoClaims,
  sincronizarClaims,
} from "../services/SincronizacaoClaims.service";

const E2E_MOCK_AUTH_ENABLED = import.meta.env.VITE_E2E_MOCK_AUTH === "true";
const E2E_AUTH_STORAGE_KEY = "__SIE_E2E_AUTH_MOCK__";

const getE2EAuthPayload = () => {
  if (!E2E_MOCK_AUTH_ENABLED || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(E2E_AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.warn("⚠️ [E2E AUTH] Payload inválido no localStorage:", error?.message);
    return null;
  }
};

// Sistema de monitoramento para produção
const logAuthEvent = (event) => {
  if (window.productionMonitor && import.meta.env.MODE === 'production') {
    window.productionMonitor.logAuthEvent(event);
  }
};

  // Cache para evitar múltiplas chamadas da API
  let urlDetailsCache = null;
  let urlDetailsPromise = null;
  let urlDetailsAttempts = 0;
  const MAX_URL_DETAILS_ATTEMPTS = 3;

  // Cache para validação de certificados de faixas coloridas (12 horas)
  let certificadosFaixasColoridasCache = {
    data: null,
    timestamp: null,
    federacaoId: null
  };
  const CACHE_DURATION_CERTIFICADOS = 12 * 60 * 60 * 1000; // 12 horas em millisegundos

const AuthContext = createContext({
  currentUser: null,
  domainDetails: null,
  loading: true, // ✅ Estado de loading para componentes saberem quando está carregando
  sessionWarning: false,
  federacaoMismatch: null,
  changePortal: () => Promise,
  register: () => Promise,
  login: () => Promise,
  logout: () => Promise,
  existEmail: () => Promise,
  changePassword: () => Promise,
  changeEmail: () => Promise,
  changeName: () => Promise,
  navigateToPortal: () => Promise,
});

export const useAuth = () => useContext(AuthContext);

function AuthContextProviderComponent({ children }) {

  const [domainDetails, setDomainDetails] = useState(null);

  // O portal de acesso vai em toda chamada a /api/tarefas (X-Portal-Id) — o backend
  // liga/desliga o CRM por portal. Publicado aqui, onde o domainDetails nasce, para
  // o Tarefas.service não depender de cada tela repassá-lo (ver services/crmPortal.js).
  const portalDeAcessoId = domainDetails?.domainId || null;
  useEffect(() => {
    definirPortalCrm(portalDeAcessoId);
  }, [portalDeAcessoId]);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [loading, setLoading] = useState(true); // ✅ Controle de loading do AuthContext
  // Referência para controle de montagem do componente
  const isMountedRef = useRef(true);

  // Controle de sessão até meia-noite (00:00:00)
  const loginTimeKey = 'cbtkd_login_time';
  const [sessionWarning, setSessionWarning] = useState(false);
  const warningNotified = useRef(false);

  // Aviso de filiação: usuário logado em portal de federação à qual NÃO é filiado
  // e que também NÃO possui grupos de permissão nesse portal (não é admin).
  // null = sem aviso; objeto = dados para o modal de redirecionamento/logout.
  const [federacaoMismatch, setFederacaoMismatch] = useState(null);

  const applyE2EAuthState = () => {
    const payload = getE2EAuthPayload();

    if (payload?.currentUser) {
      setCurrentUser(payload.currentUser);
    } else {
      setCurrentUser(undefined);
    }

    setDomainDetails(
      payload?.domainDetails || {
        domainId: "localhost-e2e",
        domainDisplayName: "E2E Local",
        domain: "localhost",
        subDomain: "",
        isLocal: true,
      }
    );

    setLoading(false);
  };

  // Função para obter o timestamp da próxima meia-noite
  const getNextMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0); // Próxima meia-noite
    return midnight.getTime();
  };



  // Função helper para obter urlDetails com cache e circuit breaker
  const getCachedUrlDetails = async () => {

    if (urlDetailsCache) {
      return urlDetailsCache;
    }

    // Circuit breaker: se excedeu tentativas, retorna fallback
    if (urlDetailsAttempts >= MAX_URL_DETAILS_ATTEMPTS) {
      const fallbackDomainDetails = {
        domainId: 'localhost-fallback',
        domainDisplayName: 'Desenvolvimento Local',
        isLocal: true,
        error: 'Exceeded max attempts'
      };
      urlDetailsCache = fallbackDomainDetails;
      return fallbackDomainDetails;
    }

    if (urlDetailsPromise) {
      return urlDetailsPromise;
    }

    urlDetailsAttempts++;
    
    urlDetailsPromise = getUrlDetails()
      .then((result) => {
        urlDetailsCache = result;
        urlDetailsPromise = null;
        // Reset attempts on success
        urlDetailsAttempts = 0;
        return result;
      })
      .catch((error) => {
        console.error("❌ [AUTH CACHE] urlDetails failed, attempt", urlDetailsAttempts, "of", MAX_URL_DETAILS_ATTEMPTS);
        console.error("❌ [AUTH CACHE] Error details:", {
          message: error.message,
          code: error.code,
          timestamp: new Date().toISOString()
        });
        
        urlDetailsPromise = null;
        
        // Se atingiu max tentativas, use fallback
        if (urlDetailsAttempts >= MAX_URL_DETAILS_ATTEMPTS) {
          const fallbackDomainDetails = {
            domainId: 'localhost-fallback',
            domainDisplayName: 'Desenvolvimento Local (Fallback)',
            isLocal: true,
            error: 'Max attempts exceeded',
            originalError: error.message
          };
          urlDetailsCache = fallbackDomainDetails;
          return fallbackDomainDetails;
        }
        
        throw error;
      });

    return urlDetailsPromise;
  };

  useEffect(() => {
    if (!E2E_MOCK_AUTH_ENABLED) {
      return undefined;
    }

    applyE2EAuthState();

    const syncE2EState = () => applyE2EAuthState();

    window.addEventListener("storage", syncE2EState);
    window.addEventListener("__SIE_E2E_AUTH_UPDATED__", syncE2EState);

    return () => {
      window.removeEventListener("storage", syncE2EState);
      window.removeEventListener("__SIE_E2E_AUTH_UPDATED__", syncE2EState);
    };
  }, []);

  // Ref independente apenas para o efeito de carregamento do domínio.
  // Não usa isMountedRef para não interferir com o fluxo de auth.
  const isDomainEffectActiveRef = useRef(true);

  useEffect(() => {
    isDomainEffectActiveRef.current = true;
    
    if (domainDetails === null && urlDetailsAttempts < MAX_URL_DETAILS_ATTEMPTS) {
      // Debounce para evitar chamadas múltiplas
      const timeoutId = setTimeout(() => {
        getCachedUrlDetails().then((urlDetails) => {
          if (isDomainEffectActiveRef.current) {
            setDomainDetails(urlDetails);
          }
        }).catch((error) => {
          console.error("❌ [AUTH DIAGNOSTIC] getCachedUrlDetails failed:", {
            error: error.message,
            code: error.code,
            stack: error.stack,
            timestamp: new Date().toISOString()
          });
          
          if (urlDetailsAttempts < MAX_URL_DETAILS_ATTEMPTS) {
            setTimeout(() => {
              if (isDomainEffectActiveRef.current && domainDetails === null) {
                getCachedUrlDetails().then((urlDetails) => {
                  if (isDomainEffectActiveRef.current) {
                    setDomainDetails(urlDetails);
                  }
                }).catch(() => {
                  if (isDomainEffectActiveRef.current) {
                    setDomainDetails({
                      domainId: 'localhost-error-fallback',
                      domainDisplayName: 'Desenvolvimento Local (Error)',
                      isLocal: true,
                      error: 'Connection failed'
                    });
                  }
                });
              }
            }, 5000);
          }
        });
      }, 100);
      
      return () => {
        isDomainEffectActiveRef.current = false;
        clearTimeout(timeoutId);
      };
    }
    
    return () => {
      isDomainEffectActiveRef.current = false;
    };
  }, [domainDetails]);

  /**
   * Verifica se a federação tem taxa ativa de certificados de faixas coloridas
   * Retorna true se a condição for atendida, false caso contrário
   * Usa cache de 12 horas para performance
   */
  const verificarTaxaCertificadosFaixasColoridas = async (federacaoId) => {
    try {
      // Verificar se há cache válido para esta federação
      const now = Date.now();
      if (
        certificadosFaixasColoridasCache.data !== null &&
        certificadosFaixasColoridasCache.federacaoId === federacaoId &&
        certificadosFaixasColoridasCache.timestamp &&
        (now - certificadosFaixasColoridasCache.timestamp) < CACHE_DURATION_CERTIFICADOS
      ) {
        return certificadosFaixasColoridasCache.data;
      }
      
      // Buscar serviços ativos da federação usando GenericVinculadoService
      // que permite usar where() diretamente para mais flexibilidade
      const { where } = await import("firebase/firestore");
      
      const conditions = [
        where("vinculo.idEntidade", "==", federacaoId),
        where("metadados.active", "==", true),
        where("dadosGerais.category", "==", "Certificado de faixas coloridas")
      ];

      const servicosResult = await GenericVinculadoService.getCollectionByConditions(
        "services",
        conditions
      );

      // getCollectionByConditions retorna [docs, count]
      const servicosData = servicosResult[0];

      if (!servicosData || servicosData.length === 0) {
        // Cachear resultado negativo
        certificadosFaixasColoridasCache = {
          data: false,
          timestamp: now,
          federacaoId: federacaoId
        };
        return false;
      }

      // Como já filtramos por categoria e active=true, se encontrou documentos, existe taxa ativa
      const temTaxaAtiva = servicosData.length > 0;

      // Cachear resultado
      certificadosFaixasColoridasCache = {
        data: temTaxaAtiva,
        timestamp: now,
        federacaoId: federacaoId
      };

      return temTaxaAtiva;

    } catch (error) {
      console.error(`❌ [AUTH CERTIFICADOS] Erro ao verificar taxa de certificados:`, error);
      // Em caso de erro, não cachear e retornar false (segurança)
      return false;
    }
  };

  /**
   * Verifica e adiciona automaticamente o usuário ao grupo padrão configurado
   * apenas se ele possuir filiação à federação do portal atual OU, no portal
   * CBTKD, se possuir filiação a alguma federação (ser filiado de fato).
   * @param {Object} userConfig - Objeto de configuração do usuário
   * @param {string} portalId - ID do portal atual
   * @param {Object} praticanteData - Dados do praticante (com idEntidade da federação)
   * @returns {Promise<Object>} - userConfig atualizado (caso tenha sido modificado)
   */
  const checkAndAddToDefaultGroup = async (userConfig, portalId, praticanteData) => {
    try {
      // 1. Buscar configuração do grupo padrão
      const configDoc = await GenericService.getOne("configuracoes", "padrao");
      const gruposPadraoId = configDoc?.standardRules?.associados;

      if (!gruposPadraoId) {
        console.warn(`⚠️ [AUTO-GROUP] Sem gruposPadraoId configurado (standardRules.associados ausente)`);
        return userConfig;
      }

      // 2. Verificar se o usuário já possui o grupo para este portal
      const userGroupsForPortal = userConfig.usersgroupsowned?.find(
        (portal) => portal.portalID === portalId
      );

      if (userGroupsForPortal?.groupsOwned?.includes(gruposPadraoId)) {
        return userConfig;
      }

      // 3. Verificar condições: o usuário só ganha o grupo padrão se possuir
      // filiação à federação do portal que está logando OU, no portal CBTKD,
      // se possuir filiação a alguma federação (ou seja, ser filiado de fato).
      const userFederacaoId = praticanteData?.dadosFiliacao?.federacaoID || "";
      const possuiFiliacao = userFederacaoId !== "";
      const isPortalCBTKD = portalId === CBTKD_ID;
      const isOwnFederation =
        userFederacaoId === portalId ||
        praticanteData?.idEntidade === portalId;

      if (!isOwnFederation && !(isPortalCBTKD && possuiFiliacao)) {
        return userConfig;
      }

      // 4. Conceder o grupo padrão pela Cloud Function.
      //
      // Este trecho montava a nova lista aqui e gravava direto em `users`. Desde
      // 04/09/2026 a regra do Firestore exige 100/102 para escrever
      // `usersgroupsowned` — que o usuário comum não tem, e é justamente ele que
      // passa por aqui. `garantirGrupoPadrao` roda com Admin SDK, só concede o
      // grupo "Associados", só ACRESCENTA e é idempotente, então a leitura-antes-
      // de-escrever que existia aqui (para não perder grupos) deixou de ser
      // necessária: quem garante isso agora é a transação do servidor.
      const resultado = await garantirGrupoPadrao(portalId);
      const updatedGroupsOwned = resultado?.usersgroupsowned
        || userConfig.usersgroupsowned
        || [];

      // Recarregar permissões para aplicar as rules do novo grupo
      const updatedRulesOwned = await UsersService.getRulesOwnedUnderPortalID(
        userConfig.uid,
        portalId
      );

      // Atualizar userConfig com novos dados.
      //
      // ⚠️ UNIÃO, nunca substituição. Esta função roda DEPOIS do bloco de regras
      // por graduação (Mestre/Professor), e aquelas regras existem só na sessão —
      // `garantirGrupoPadrao` não as grava em `usersgroupsowned`, então
      // `getRulesOwnedUnderPortalID` não as devolve. Um `=` aqui apagava o bônus
      // inteiro do mestre no PRIMEIRO login de um portal (o único caso em que
      // este trecho executa; quem já tem o grupo retorna no passo 2), e ele só
      // voltava no login seguinte. Foi o que escondeu o acesso de um 5º Dan ao
      // menu do SIE — ver docs/DIAGNOSTICO-ACESSO-MENU-SIE-FETRON.md.
      //
      // A união também preserva a subtração feita pelo bloco de certificados
      // (2500/2510 quando a federação não tem taxa ativa): `updatedRulesOwned` é
      // só o grupo "Associados", que não contém essas regras, e as duas listas
      // são deste mesmo portal — não há vazamento de permissão de outro portal.
      userConfig.usersgroupsowned = updatedGroupsOwned;
      userConfig.rulesOwned = [...new Set([...(userConfig.rulesOwned || []), ...updatedRulesOwned])];

      return userConfig;

    } catch (error) {
      console.warn(`⚠️ [AUTO-GROUP] Erro ao adicionar ao grupo padrão (não bloqueante):`, error.message);
      // Em caso de erro, retornar userConfig sem modificação e continuar autenticação normalmente
      return userConfig;
    }
  };

  useEffect(() => {
    // Definindo o componente como montado
    isMountedRef.current = true;

    if (E2E_MOCK_AUTH_ENABLED) {
      applyE2EAuthState();
      return () => {
        isMountedRef.current = false;
      };
    }
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      try {
        if (!user) {
          // Verificar se o componente ainda está montado
          if (isMountedRef.current) {
            setCurrentUser(undefined);
            setFederacaoMismatch(null);
            esquecerSincronizacaoClaims();
            refreshDomainDetails();
          }
          setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)
          return;
        }

        if (!user.uid) {
          // Verificar se o componente ainda está montado
          if (isMountedRef.current) {
            setCurrentUser(undefined);
            refreshDomainDetails();
          }
          setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)
          return;
        }

        getCachedUrlDetails().then(async (urlDetails) => {
          // Verificar se o componente ainda está montado
          if (!isMountedRef.current) return;
          
          if (!urlDetails) {
            if (isMountedRef.current) {
              setCurrentUser(undefined);
              refreshDomainDetails();
            }
            setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)
            return;
          }

          setDomainDetails(urlDetails);

          // Determinar o ID atual do usuário
          let currentID = user.uid;
          let isIDForced = false;

          // 🧪 SIMULAÇÃO DE LOGIN (somente desenvolvimento local, escopo: aba atual)
          // Para impersonar outro usuário/praticante em desenvolvimento local,
          // use sessionStorage (vale APENAS na aba atual — outras abas/janelas
          // continuam com seu login real). No console do navegador:
          //   sessionStorage.setItem("__SIE_IMPERSONATE_UID__", "UID_ALVO_AQUI")
          //   location.reload()
          // E para desativar:
          //   sessionStorage.removeItem("__SIE_IMPERSONATE_UID__")
          //   location.reload()
          // Aceita: localhost, *.localhost (ex.: fctkd.localhost) e 127.0.0.1.
          try {
            if (typeof window !== "undefined") {
              const host = window.location.hostname;
              const isDevHost =
                host === "localhost" ||
                host.endsWith(".localhost") ||
                host === "127.0.0.1";
              if (isDevHost) {
                const impersonateUid = window.sessionStorage.getItem("__SIE_IMPERSONATE_UID__");
                if (impersonateUid && impersonateUid !== currentID) {
                  console.warn(
                    `🧪 [AUTH IMPERSONATE] Forçando UID nesta aba (${host}): ${currentID} → ${impersonateUid}`
                  );
                  currentID = impersonateUid;
                  isIDForced = true;
                }
              }
            }
          } catch (impersonateErr) {
            console.warn("⚠️ [AUTH IMPERSONATE] Erro ao ler sessionStorage:", impersonateErr?.message);
          }

          try {
            // Buscar as permissões do usuário no portal atual
            let rulesOwnedRetrieved = await UsersService.getRulesOwnedUnderPortalID(currentID, urlDetails.domainId);
            
            // Verificar se o componente ainda está montado
            if (!isMountedRef.current) return;

            // Buscar configurações padrão
            const configuracoesRetrieved = await GenericService.getOne("configuracoes", "padrao");
            
            // Verificar se o componente ainda está montado
            if (!isMountedRef.current) return;
            
            // console.log("configuracoesRetrieved.standardRules=", configuracoesRetrieved.standardRules);

            // Buscar informações do praticante
            const praticanteRetrieved = await GenericService.getOne("praticantes", currentID);
            
            // Verificar se o componente ainda está montado
            if (!isMountedRef.current) return;

            // ============================================
            // 🔄 SINCRONIZAÇÃO DE E-MAIL (Auth → Firestore)
            // Auth é a fonte da verdade. Se praticantes ou users tiverem
            // e-mail divergente, corrigir silenciosamente.
            // ============================================
            if (praticanteRetrieved && user.email && !isIDForced) {
              const praticanteEmail = praticanteRetrieved.dadosContato?.email?.toLowerCase();
              const authEmail = user.email.toLowerCase();

              if (praticanteEmail && praticanteEmail !== authEmail) {
                console.warn(`⚠️ [AUTH SYNC] E-mail divergente em praticantes: auth=${authEmail}, praticantes=${praticanteEmail}. Corrigindo...`);
                try {
                  await GenericService.updateObject("praticantes", currentID, {
                    dadosContato: { ...praticanteRetrieved.dadosContato, email: authEmail }
                  });
                  praticanteRetrieved.dadosContato.email = authEmail;
                  console.log("✅ [AUTH SYNC] E-mail em praticantes corrigido.");
                } catch (syncErr) {
                  console.error("❌ [AUTH SYNC] Erro ao corrigir e-mail em praticantes:", syncErr.message);
                }
              }

              // Verificar também a coleção users
              try {
                const userDocForEmailCheck = await UsersService.getOne(currentID);
                const usersEmail = userDocForEmailCheck?.dadosContato?.email?.toLowerCase();
                if (usersEmail && usersEmail !== authEmail) {
                  console.warn(`⚠️ [AUTH SYNC] E-mail divergente em users: auth=${authEmail}, users=${usersEmail}. Corrigindo...`);
                  await GenericService.updateObject("users", currentID, {
                    dadosContato: { ...(userDocForEmailCheck?.dadosContato || {}), email: authEmail }
                  });
                  console.log("✅ [AUTH SYNC] E-mail em users corrigido.");
                }
              } catch (syncErr) {
                console.error("❌ [AUTH SYNC] Erro ao corrigir e-mail em users:", syncErr.message);
              }
            }
            
            // 🔧 RECUPERAÇÃO AUTOMÁTICA: Verificar os 2 registros necessários
            // 1. Firebase Authentication
            // 2. Coleção users no Firestore
            // Não executar quando o ID foi forçado para testes (evita conflito de email/UID)
            if (praticanteRetrieved && !isIDForced) {
              let authExists = false;
              let firestoreExists = false;
              // Quando a API retorna 403 (não disponível/sem autorização no ambiente atual),
              // consideramos que a verificação não é possível e pulamos a recovery inteira.
              let apiIndisponivel = false;
              
              // ============================================
              // ETAPA 1: Verificar Firebase Authentication
              // ============================================
              
              try {
                const authCheck = await UsersService.checkIfEmailExistsAndID(currentID, user.email);
                
                if (authCheck && authCheck[0] === 1) {
                  authExists = true;
                } else {
                  authExists = false;
                }
              } catch (authError) {
                if (authError?.response?.status === 403 || authError?.status === 403) {
                  // API indisponível neste ambiente (emulador não está rodando) — ignorar silenciosamente
                  apiIndisponivel = true;
                } else {
                  console.error("❌ [AUTH RECOVERY] Erro ao verificar Firebase Auth:", authError);
                }
                authExists = false;
              }
              
              // Criar no Firebase Auth se não existir e API estiver disponível
              if (!apiIndisponivel && !authExists && user.email) {
                try {
                  await UsersService.createAuthenticationUser(
                    currentID,
                    user.email,
                    praticanteRetrieved.dadosPessoais?.name || "Usuário"
                  );
                } catch (createAuthError) {
                  if (createAuthError?.response?.status !== 403 && createAuthError?.status !== 403) {
                    console.error("❌ [AUTH RECOVERY] Erro ao criar Firebase Auth:", createAuthError);
                  }
                  // Não bloquear - continuar com Firestore
                }
              }
              
              // ============================================
              // ETAPA 2: Verificar coleção users (Firestore)
              // ============================================
              
              // Só existe UM motivo para recriar o documento: ter certeza de que ele
              // não existe. Qualquer outra falha de leitura (rede caindo, cliente
              // offline, token de auth ainda não propagado) NÃO prova ausência — e
              // tratá-la como ausência custou caro: entre fev e set/2026 a recovery
              // sobrescreveu 31 cadastros legítimos, apagando portais, grupos e dados
              // pessoais. Ver docs/INCIDENTE-AUTH-RECOVERY-USERS.md.
              let leituraInconclusiva = false;
              try {
                const userRecord = await UsersService.getOne(currentID);

                // Verificar se o objeto retornado tem conteúdo real
                if (userRecord && typeof userRecord === 'object' && Object.keys(userRecord).length > 0) {
                  firestoreExists = true;
                } else {
                  // Documento vazio não é o mesmo que documento ausente: na dúvida, não mexer.
                  console.warn("⚠️ [AUTH RECOVERY] Firestore users: documento vazio ou inválido — recovery pulada por segurança");
                  leituraInconclusiva = true;
                }
              } catch (getUserError) {
                if (getUserError.message && getUserError.message.includes("não encontrado")) {
                  console.warn("⚠️ [AUTH RECOVERY] Firestore users: Documento NÃO existe (confirmado)");
                  firestoreExists = false;
                } else {
                  console.error("❌ [AUTH RECOVERY] Falha ao consultar Firestore — recovery pulada por segurança:", getUserError);
                  leituraInconclusiva = true;
                }
              }

              // Criar documento no Firestore só quando a ausência foi confirmada
              if (!firestoreExists && !leituraInconclusiva) {
                console.warn("⚠️ [AUTH RECOVERY] Firestore users: Documento NÃO existe! Criando...");
                
                try {
                  // O documento nasce SEM grupo: a regra de `users` só aceita
                  // `usersgroupsowned` vazio vindo do próprio usuário, e é a Cloud
                  // Function `garantirGrupoPadrao` que concede o grupo padrão logo
                  // abaixo. Antes daqui saía um fallback para "DUm9PQZB2NQhMhDIiVJP",
                  // grupo que NÃO EXISTE em `usersgroups` — se a configuração
                  // falhasse ao carregar, o usuário recebia um vínculo que não
                  // resolve permissão nenhuma e ficava inutilizável sem erro visível.
                  const usersgroupsowned = [];
                  const federacaoId = praticanteRetrieved.dadosFiliacao?.federacaoID;
                  
                  // Criar objeto de usuário
                  const recoveredUserData = {
                    dadosPessoais: {
                      name: praticanteRetrieved.dadosPessoais?.name || user.displayName || "Usuário",
                      searchableName: normalizarSearchableName(praticanteRetrieved.dadosPessoais?.name || user.displayName || "Usuário"),
                      cpf: praticanteRetrieved.dadosPessoais?.cpf || "",
                      birthdate: praticanteRetrieved.dadosPessoais?.birthdate || null,
                    },
                    dadosContato: {
                      email: user.email || praticanteRetrieved.dadosContato?.email || "",
                      celular: praticanteRetrieved.dadosContato?.celular || "",
                    },
                    metadados: {
                      ownerid: currentID,
                      owner: praticanteRetrieved.dadosPessoais?.name || user.displayName || "Sistema",
                      creation: Timestamp.fromDate(new Date()),
                      date: Timestamp.fromDate(new Date()),
                      version: 1,
                      active: true,
                      // Sem successorId o documento fica INVISÍVEL em toda a listagem do
                      // sistema: as consultas filtram por successorId == "" e no Firestore
                      // um campo ausente nunca casa com uma igualdade.
                      successorId: "",
                      recoveredByAuthContext: true, // Flag indicando recuperação automática
                    },
                    usersgroupsowned: usersgroupsowned,
                  };
                  
                  // Criar registro users
                  await UsersService.createWithID(currentID, recoveredUserData, true);

                  // Grupo padrão pela Cloud Function (o client não escreve
                  // `usersgroupsowned`). Falhar aqui não pode derrubar o login: o
                  // vínculo é recuperável na entrada seguinte.
                  try {
                    await garantirGrupoPadrao(
                      federacaoId && federacaoId !== CBTKD_ID ? federacaoId : urlDetails.domainId,
                    );
                  } catch (e) {
                    console.error("❌ [AUTH RECOVERY] garantirGrupoPadrao falhou:", e);
                  }
                  
                  // 🔄 RE-FETCH: Documento recém-criado — re-buscar permissões para garantir
                  // que o grupo padrão foi aplicado antes de montar o userConfig
                  if (isMountedRef.current) {
                    const rulesFetched = await UsersService.getRulesOwnedUnderPortalID(currentID, urlDetails.domainId);
                    rulesOwnedRetrieved = Array.isArray(rulesFetched) ? rulesFetched : [];
                  }
                } catch (recoveryError) {
                  console.error("❌ [AUTH RECOVERY] Erro ao criar Firestore users:", recoveryError);
                  // Não bloquear o login
                }
              }
            }

            // Configurar o usuário com as informações base
            // Ler isSuperAdmin do documento do usuário no Firestore
            let isSuperAdminValue = false;
            // Login criado pela importação com a data de nascimento como senha:
            // o app exige a troca antes de liberar o resto (ExigirTrocaDeSenha).
            let trocarSenhaValue = false;
            try {
              const userDocData = await UsersService.getOne(currentID);
              isSuperAdminValue = userDocData?.isSuperAdmin === true;
              trocarSenhaValue = userDocData?.trocarSenhaNoProximoAcesso === true;
            } catch (_) {
              // não bloquear o login se a leitura falhar — a claim do token (abaixo) ainda vale
            }
            // A marca também vem no TOKEN (claim `trocarSenha`, posta pela importação):
            // não depende da leitura acima, que falhando deixava a troca passar.
            try {
              const resultadoToken = await getAuth().currentUser?.getIdTokenResult();
              if (resultadoToken?.claims?.trocarSenha === true) trocarSenhaValue = true;
            } catch (_) {
              // não bloquear o login se a leitura falhar
            }

            let userConfig = {
              ...user,
              uid: currentID,
              rulesOwned: rulesOwnedRetrieved,
              urlImagemID: praticanteRetrieved?.dadosPessoais?.urlImagemID || "",
              federacaoID: praticanteRetrieved?.dadosFiliacao?.federacaoID || "", // ✅ ID da federação do usuário para filtro estadual
              vinculadoAEntidadeID: praticanteRetrieved?.dadosFiliacao?.vinculadoAEntidadeID || "", // ✅ Entidade/Associação à qual o praticante é filiado
              entidadesLote: [], // ✅ Entidades onde é responsável técnico (preenchido via async)
              isSuperAdmin: isSuperAdminValue,
              trocarSenhaNoProximoAcesso: trocarSenhaValue,
            };
            
            // ✅ Carregar entidades onde é responsável técnico (para inscrição em lote)
            try {
              const entidadesLegacy = praticanteRetrieved?.dadosColaborador?.administrativo?.entidadesOndeEhResponsavelTecnico || [];
              const entidadesSubcollection = await PraticantesService.getEntidadesOndeEhResponsavelTecnicoFromPraticanteId(currentID);
              if (!isMountedRef.current) return;
              const entidadesSet = new Set([
                ...(userConfig.vinculadoAEntidadeID ? [userConfig.vinculadoAEntidadeID] : []),
                ...entidadesLegacy,
                ...entidadesSubcollection,
              ]);
              userConfig.entidadesLote = [...entidadesSet].filter(Boolean);
            } catch (e) {
              console.warn("[authContext] Erro ao carregar entidadesLote:", e);
              userConfig.entidadesLote = userConfig.vinculadoAEntidadeID ? [userConfig.vinculadoAEntidadeID] : [];
            }

            if (praticanteRetrieved?.dadosPessoais) {
              userConfig.displayName = praticanteRetrieved.dadosPessoais.name || "";
            }

            if (praticanteRetrieved?.dadosTaekwondo) {
              userConfig.maxGraduacao = praticanteRetrieved.dadosTaekwondo.maxGraduacao || 0;
              userConfig.MaxGraduacao = praticanteRetrieved.dadosTaekwondo.MaxGraduacao || 0;
            }

            if (praticanteRetrieved?.dadosEndereco) {
              userConfig.logradouroMunicipio = praticanteRetrieved.dadosEndereco.logradouroMunicipio || "";
              userConfig.logradouroUF = praticanteRetrieved.dadosEndereco.logradouroUF || "";

            }
            // Adicionar informações de empregador se disponíveis
            // 🛡️ Suporte a administrativo como array (multi-entidade) ou objeto legado
            const adm = praticanteRetrieved?.dadosColaborador?.administrativo;
            const admObj = Array.isArray(adm) ? adm[0] : adm;
            if (admObj?.idEmpregador) {
              userConfig.idEmpregador = admObj.idEmpregador;
              userConfig.nomeEmpregador = admObj.nomeEmpregador;
            }
            userConfig.cbtkdactive = praticanteRetrieved?.metadados?.active || false;
            userConfig.federativeactive = praticanteRetrieved?.metadados?.federativeactive || false;

            // Regras de graduação (Mestre/Professor) só podem ser aplicadas no portal
            // referente à federação onde o praticante tem filiação. Em qualquer outro
            // portal (inclusive o CBTKD nacional, quando ele não é filiado ao CBTKD),
            // essas permissões NÃO devem ser concedidas.
            const pertenceFederacao = praticanteRetrieved?.dadosFiliacao?.federacaoID === urlDetails.domainId;
            const deveAplicarRegraGraduacao = pertenceFederacao && !!praticanteRetrieved?.dadosTaekwondo;

            if (deveAplicarRegraGraduacao) {
              let graduacao = praticanteRetrieved.dadosTaekwondo.maxGraduacao;

              try {
                // Adicionar regras especiais baseadas na graduação
                if (graduacao >= 14) {
                  const userGroupMestres = await UsersGroupsService.getOne(configuracoesRetrieved.standardRules.mestres);
                  
                  // Verificar se o componente ainda está montado
                  if (!isMountedRef.current) return;
                  
                  userConfig.rulesOwned = [...new Set([...rulesOwnedRetrieved, ...userGroupMestres.rulesowned])];
                  if (import.meta.env.DEV) console.log(`🎓 [AUTH GRADUACAO] Mestre (${graduacao}) — ${userGroupMestres.rulesowned?.length ?? 0} rules adicionadas. Total: ${userConfig.rulesOwned.length}`);
                }
                else if (graduacao > 10) {
                  const userGroupsProfessores = await UsersGroupsService.getOne(configuracoesRetrieved.standardRules.professores);
                  
                  // Verificar se o componente ainda está montado
                  if (!isMountedRef.current) return;
                  
                  userConfig.rulesOwned = [...new Set([...rulesOwnedRetrieved, ...userGroupsProfessores.rulesowned])];
                  if (import.meta.env.DEV) console.log(`🎓 [AUTH GRADUACAO] Professor (${graduacao}) — ${userGroupsProfessores.rulesowned?.length ?? 0} rules adicionadas. Total: ${userConfig.rulesOwned.length}`);
                }
              } catch (error) {
                console.error(`❌ [AUTH GRADUACAO] Erro ao carregar regras de graduação:`, error.message);
                // Mantém as regras básicas em caso de erro
              }
            }

            // ⚠️ VALIDAÇÃO: Certificados de Faixas Coloridas (Rules 2500 e 2510)
            // Apenas para federações (subDomain !== "")
            const RULE_SOLICITAR_CERTIFICADO = 2500;
            const RULE_CONFIGURACOES_CERTIFICADO = 2510;
            
            if (urlDetails.subDomain && urlDetails.subDomain !== "") {
              // console.log(`🔍 [AUTH CERTIFICADOS] Portal de federação detectado (${urlDetails.subDomain}) - validando rules 2500 e 2510`);
              
              // Verificar se usuário tem as rules de certificados
              const temRuleCertificado = userConfig.rulesOwned.includes(RULE_SOLICITAR_CERTIFICADO);
              const temRuleConfiguracoes = userConfig.rulesOwned.includes(RULE_CONFIGURACOES_CERTIFICADO);
              
              if (temRuleCertificado || temRuleConfiguracoes) {
                // console.log(`🔍 [AUTH CERTIFICADOS] Usuário possui rules de certificados - verificando condições...`);
                
                // Verificar se a federação tem taxa ativa de certificados de faixas coloridas
                const temTaxaAtiva = await verificarTaxaCertificadosFaixasColoridas(urlDetails.domainId);
                
                // Verificar se o componente ainda está montado
                if (!isMountedRef.current) return;
                
                if (!temTaxaAtiva) {
                  // console.warn(`⚠️ [AUTH CERTIFICADOS] Federação ${urlDetails.subDomain} NÃO possui taxa ativa - removendo rules 2500 e 2510`);
                  
                  // Remover rules 2500 e 2510
                  userConfig.rulesOwned = userConfig.rulesOwned.filter(
                    rule => rule !== RULE_SOLICITAR_CERTIFICADO && rule !== RULE_CONFIGURACOES_CERTIFICADO
                  );
                  
                  // console.log(`✅ [AUTH CERTIFICADOS] Rules 2500 e 2510 removidas. Total de rules restantes: ${userConfig.rulesOwned.length}`);
                } else {
                  // console.log(`✅ [AUTH CERTIFICADOS] Condições atendidas - rules 2500 e 2510 mantidas`);
                }
              } else {
                // console.log(`ℹ️ [AUTH CERTIFICADOS] Usuário não possui rules 2500 ou 2510 - sem validação necessária`);
              }
            } else {
              // console.log(`ℹ️ [AUTH CERTIFICADOS] Portal principal CBTKD (sem subdomínio) - sem validação de rules 2500/2510`);
            }

            // 🔄 AUTO-ATRIBUIÇÃO: Adicionar automaticamente ao grupo padrão se aplicável
            // Verifica e adiciona o usuário ao grupo padrão configurado (se no CBTKD ou própria federação)
            userConfig = await checkAndAddToDefaultGroup(
              userConfig,
              urlDetails.domainId,
              praticanteRetrieved
            );

            // Verificar se o componente ainda está montado
            if (!isMountedRef.current) return;
            
            // 🛡️ SAFETY NET: Se após checkAndAddToDefaultGroup rulesOwned ainda estiver vazio,
            // fazer um último re-fetch para garantir que as permissões foram carregadas
            if (!userConfig.rulesOwned || userConfig.rulesOwned.length === 0) {
              console.warn(`⚠️ [AUTH PERMISSIONS] rulesOwned AINDA VAZIO após checkAndAddToDefaultGroup — executando safety re-fetch final...`);
              try {
                const safetyRules = await UsersService.getRulesOwnedUnderPortalID(currentID, urlDetails.domainId);
                userConfig.rulesOwned = Array.isArray(safetyRules) ? safetyRules : [];
                console.log(`🔐 [AUTH PERMISSIONS] Safety re-fetch: ${userConfig.rulesOwned.length} rules — ${userConfig.rulesOwned.join(', ') || 'NENHUMA (investigar usersgroups)'}`);
              } catch (safetyError) {
                console.error(`❌ [AUTH PERMISSIONS] Safety re-fetch falhou:`, safetyError.message);
              }
            }

            // ============================================
            // 🏛️ VALIDAÇÃO DE FILIAÇÃO EM PORTAL DE FEDERAÇÃO
            // Se o usuário logou em um portal de federação (subDomain !== "")
            // ao qual NÃO é filiado e NÃO possui grupos de permissão nesse
            // portal (ou seja, não é admin/colaborador desse portal), exibir
            // aviso oferecendo redirecioná-lo ao portal da sua federação.
            // Se recusar, será deslogado automaticamente (tratado no modal).
            // ============================================
            try {
              const ehPortalFederacao =
                !!urlDetails.subDomain && urlDetails.subDomain !== "" && urlDetails.domainId !== CBTKD_ID;

              // A filiação do praticante pode estar gravada de duas formas (legado):
              //  - dadosFiliacao.federacaoID (ID da entidade) — formato novo
              //  - dadosFiliacao.federacao (sigla da federação) — formato antigo
              const userFederacaoIdRaw = praticanteRetrieved?.dadosFiliacao?.federacaoID || "";
              const userFederacaoSigla = praticanteRetrieved?.dadosFiliacao?.federacao || "";

              // Considera filiado se o ID OU a sigla baterem com o portal atual.
              const filiadoPorId = userFederacaoIdRaw !== "" && userFederacaoIdRaw === urlDetails.domainId;
              const filiadoPorSigla =
                userFederacaoSigla !== "" && !!urlDetails.sigla && userFederacaoSigla === urlDetails.sigla;
              const ehFiliado = filiadoPorId || filiadoPorSigla;

              const possuiFederacaoPropria = userFederacaoIdRaw !== "" || userFederacaoSigla !== "";

              // Exceção: usuário com acesso administrativo neste portal (super admin,
              // permissões efetivas ou grupos explícitos atribuídos a este portal).
              const temAcessoNestePortal =
                userConfig.isSuperAdmin === true ||
                (Array.isArray(userConfig.rulesOwned) && userConfig.rulesOwned.length > 0) ||
                (Array.isArray(userConfig.usersgroupsowned) &&
                  userConfig.usersgroupsowned.some(
                    (p) => p.portalID === urlDetails.domainId && Array.isArray(p.groupsOwned) && p.groupsOwned.length > 0
                  ));

              if (ehPortalFederacao && !ehFiliado && possuiFederacaoPropria && !temAcessoNestePortal) {
                console.warn(
                  `⚠️ [AUTH FILIAÇÃO] Usuário não filiado a este portal de federação (${urlDetails.domainId}) e sem permissões — preparando aviso de redirecionamento.`
                );

                // Resolver entidade da federação do usuário (por ID ou por sigla) para
                // montar a URL de destino e o nome amigável.
                let redirectUrl = null;
                let userFederacaoNome = userFederacaoSigla || "sua federação";
                try {
                  let userFedEntityData = null;
                  if (userFederacaoIdRaw) {
                    userFedEntityData = await GenericService.getOne("entidades", userFederacaoIdRaw);
                  } else if (userFederacaoSigla) {
                    const { where, orderBy } = await import("firebase/firestore");
                    const resBySigla = await GenericVinculadoService.getCollectionByConditions("entidades", [
                      where("dadosPessoais.sigla", "==", userFederacaoSigla),
                      where("metadados.successorId", "==", ""),
                      where("metadados.active", "==", true),
                      orderBy("metadados.creation", "desc"),
                    ]);
                    if (resBySigla && resBySigla[0] && resBySigla[0].length > 0) {
                      userFedEntityData = resBySigla[0][0].data();
                    }
                  }

                  if (userFedEntityData) {
                    userFederacaoNome =
                      userFedEntityData.dadosPessoais?.name || userFedEntityData.dadosPessoais?.sigla || userFederacaoNome;
                    const userFedDomains = userFedEntityData.configuracoes?.domain || [];
                    const baseDomain = urlDetails.domain || "";
                    const matchingDomain =
                      userFedDomains.find((d) => baseDomain && d.includes(baseDomain)) || userFedDomains[0] || null;
                    if (matchingDomain) {
                      const useHttp = matchingDomain.includes("localhost") || matchingDomain.includes("127.0.0.1");
                      redirectUrl = matchingDomain.startsWith("http")
                        ? matchingDomain
                        : `${useHttp ? "http" : "https"}://${matchingDomain}`;
                    }
                  }
                } catch (fedLookupError) {
                  console.error("❌ [AUTH FILIAÇÃO] Erro ao buscar federação do usuário:", fedLookupError.message);
                }

                // Fallback: se não foi possível montar a URL do portal da federação
                // do usuário, redireciona para o portal nacional da CBTKD (domínio
                // base, sem subdomínio de federação).
                let destinoNome = userFederacaoNome;
                if (!redirectUrl) {
                  const baseDomain = urlDetails.domain || "";
                  if (baseDomain) {
                    const useHttp = baseDomain.includes("localhost") || baseDomain.includes("127.0.0.1");
                    redirectUrl = baseDomain.startsWith("http")
                      ? baseDomain
                      : `${useHttp ? "http" : "https"}://${baseDomain}`;
                  }
                  destinoNome = "CBTKD";
                }

                if (isMountedRef.current) {
                  setFederacaoMismatch({
                    portalNome: urlDetails.name || urlDetails.sigla || "esta federação",
                    destinoNome,
                    redirectUrl,
                  });
                }
              } else if (isMountedRef.current) {
                setFederacaoMismatch(null);
              }
            } catch (filiacaoError) {
              console.error("❌ [AUTH FILIAÇÃO] Erro na validação de filiação:", filiacaoError.message);
            }

            // Verificar se o componente ainda está montado antes de atualizar o estado do usuário
            if (isMountedRef.current) {
              try {
                // FAILSAFE: Validar userConfig antes de atualizar o estado
                if (!userConfig || typeof userConfig !== 'object') {
                  console.error("❌ [AUTH FAILSAFE] userConfig inválido:", userConfig);
                  setCurrentUser(undefined);
                  setLoading(false);
                  return;
                }
                
                // FAILSAFE: Garantir que rulesOwned é um array
                if (!Array.isArray(userConfig.rulesOwned)) {
                  console.warn("⚠️ [AUTH FAILSAFE] rulesOwned não é array, corrigindo...");
                  userConfig.rulesOwned = [];
                }
                
                // FAILSAFE: Garantir que usersgroupsowned é um array
                if (!Array.isArray(userConfig.usersgroupsowned)) {
                  userConfig.usersgroupsowned = [];
                }
                
                // Atualizar o estado do usuário atual
                setCurrentUser(userConfig);

                // Claims de permissão no token (ver SincronizacaoClaims.service).
                //
                // Depois do `setCurrentUser` e sem `await`: a tela não espera
                // por isto. O claim serve às regras dos bancos nomeados, não à
                // interface — que continua lendo `rulesOwned` do contexto.
                sincronizarClaims(userConfig.uid).then((r) => {
                  // D32: a senha inicial foi trocada por fora (e-mail de redefinição)
                  // e o servidor liberou a conta — não mandar para a troca de novo.
                  if (r?.trocaSenhaLiberada) {
                    setCurrentUser((atual) => (atual && atual.trocarSenhaNoProximoAcesso ? { ...atual, trocarSenhaNoProximoAcesso: false } : atual));
                  }
                });
              } catch (error) {
                console.error("❌ [AUTH FAILSAFE] Erro ao atualizar currentUser:", error);
                // Em caso de erro, definir usuário como undefined para forçar re-login
                setCurrentUser(undefined);
              }
            }
            setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)

          } catch (error) {
            console.error("[authContext] ❌ Erro ao buscar informações do usuário:", {
              error: error.message,
              stack: error.stack,
              currentID,
              timestamp: new Date().toISOString()
            });
            
            // Verificar se o componente ainda está montado
            if (isMountedRef.current) {
              try {
                // FAILSAFE: Criar objeto de usuário mínimo válido em caso de erro
                // Troca obrigatória de senha (D32) vale também aqui: o token traz a
                // claim mesmo quando a montagem do usuário falhou (code review rodada 3).
                let trocarSenhaFallback = false;
                try {
                  const tokenFallback = await getAuth().currentUser?.getIdTokenResult();
                  trocarSenhaFallback = tokenFallback?.claims?.trocarSenha === true;
                } catch (_) {
                  // sem token legível: segue sem a marca
                }
                const fallbackUser = {
                  ...user,
                  uid: currentID,
                  rulesOwned: [],
                  usersgroupsowned: [],
                  displayName: user?.displayName || 'Usuário',
                  email: user?.email || '',
                  trocarSenhaNoProximoAcesso: trocarSenhaFallback,
                };
                setCurrentUser(fallbackUser);
              } catch (fallbackError) {
                console.error("❌ [AUTH FAILSAFE] Erro crítico ao criar usuário fallback:", fallbackError);
                // Último recurso: undefined para forçar re-login
                setCurrentUser(undefined);
              }
            }
            setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)
          }
        });
      } catch (err) {
        console.log("[authContext] Erro na verificação de autenticação:", err);
        
        // Verificar se o componente ainda está montado
        if (isMountedRef.current) {
          setCurrentUser(undefined);
          refreshDomainDetails();
        }
        setLoading(false); // ✅ SEMPRE atualizar loading (fora do if)
      }
    });

    // Função de limpeza quando o componente for desmontado
    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect para controle de sessão de 24 horas
  useEffect(() => {
    if (!currentUser || !currentUser.uid) return;

    // Funções internas para evitar dependências desnecessárias
    const checkIsSessionExpired = (loginTime) => {
      try {
        const loginTimestamp = parseInt(loginTime);
        const currentTime = Date.now();
        
        // Obter a data de login e a data atual
        const loginDate = new Date(loginTimestamp);
        const currentDate = new Date(currentTime);
        
        // Verificar se mudou o dia (passou da meia-noite)
        return loginDate.toDateString() !== currentDate.toDateString();
      } catch {
        return true;
      }
    };

    const checkIsSessionNearExpiry = (loginTime) => {
      try {
        const currentTime = Date.now();
        const midnight = getNextMidnight();
        const timeUntilMidnight = midnight - currentTime;
        const warningThreshold = 2 * 60 * 60 * 1000; // 2 horas antes da meia-noite
        
        return timeUntilMidnight > 0 && timeUntilMidnight < warningThreshold;
      } catch {
        return false;
      }
    };

    const getTimeRemaining = (loginTime) => {
      try {
        const currentTime = Date.now();
        const midnight = getNextMidnight();
        const timeRemaining = midnight - currentTime;
        return Math.max(0, Math.floor(timeRemaining / (1000 * 60))); // retorna em minutos
      } catch {
        return 0;
      }
    };

    const checkSessionStatus = () => {
      const loginTime = localStorage.getItem(loginTimeKey);
      if (!loginTime) return;

      // Verificar se a sessão expirou (passou da meia-noite)
      if (checkIsSessionExpired(loginTime)) {
        // Usar signOut diretamente para evitar dependência circular
        signOut(auth).then(() => {
          localStorage.removeItem(loginTimeKey);
        });
        return;
      }

      // Verificar se está próximo de expirar e mostrar aviso
      if (checkIsSessionNearExpiry(loginTime) && !warningNotified.current) {
        const timeRemaining = getTimeRemaining(loginTime);
        const hours = Math.floor(timeRemaining / 60);
        const minutes = timeRemaining % 60;
        
        console.warn(`⚠️ [SESSION] Sua sessão expirará em ${hours}h ${minutes}min. Salve seu trabalho!`);
        
        setSessionWarning(true);
        warningNotified.current = true;
        
        // Reset do aviso após 30 minutos para mostrar novamente se necessário
        setTimeout(() => {
          warningNotified.current = false;
          setSessionWarning(false);
        }, 30 * 60 * 1000);
      }
    };

    // Verificação inicial
    checkSessionStatus();

    // Verificar a cada minuto
    const intervalId = setInterval(checkSessionStatus, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentUser, loginTimeKey]);

  // Função para resetar o cache e tentativas (útil para debug ou reconexão)
  const resetUrlDetailsCache = () => {

    urlDetailsCache = null;
    urlDetailsPromise = null;
    urlDetailsAttempts = 0;
  };

  // Função auxiliar para atualizar detalhes do domínio
  const refreshDomainDetails = () => {

    resetUrlDetailsCache();
    setDomainDetails(null);
  };

  const changePortal = async (portalName) => {
    try {
      // Verificar se domainDetails existe antes de prosseguir
      if (!domainDetails) {
        // console.log("domainDetails não está definido");
        return false;
      }

      let newPortal = await GetPortal();
      
      // Verificar se newPortal foi retornado corretamente
      if (!newPortal) {
        // console.log("Erro ao obter novo portal");
        return false;
      }

      // Atualizar domainDetails
      setDomainDetails({
        domain: domainDetails.domain,
        domainId: domainDetails.domainId,
        subDomain: domainDetails.subDomain,
        portal: newPortal.urlportal,
        portalId: newPortal.id, // Corrigido para minúsculo (id em vez de Id)
      });

      return true;
    } catch (error) {
      console.log("Erro ao mudar de portal:", error);
      return false;
    }
  };


  const existEmail = async (email) => {
    return email !== undefined && email !== null && email !== "";
  };

  const changePassword = async (lastPassword, newPassword) => {
    const user = getAuth().currentUser;
    const startTime = performance.now();
    try {
      logAuthEvent({ type: 'password_change_attempt', userId: user?.uid, email: user?.email });

      // Reautentica sem fazer signOut — preserva a sessão em caso de erro
      const credential = EmailAuthProvider.credential(user.email, lastPassword);
      await reauthenticateWithCredential(user, credential);

      // Usa a rota admin para trocar a senha (não exige nova autenticação pós-troca)
      const changed = await UsersService.changePassword(user.uid, newPassword);
      if (!changed) throw new Error("Falha ao atualizar senha no servidor");

      logAuthEvent({ type: 'password_change_success', userId: user?.uid, duration: performance.now() - startTime });
      // O servidor já apagou a marca de troca obrigatória (users e claim); o token
      // novo não traz mais a claim, e a sessão atual também deixa de exigir.
      await getAuth().currentUser?.getIdToken(true).catch(() => {});
      setCurrentUser((atual) => (atual && atual.trocarSenhaNoProximoAcesso ? { ...atual, trocarSenhaNoProximoAcesso: false } : atual));
      return true;
    } catch (error) {
      console.log("Erro ao alterar senha:", error);
      logAuthEvent({ type: 'password_change_failed', userId: user?.uid, error: error.message, duration: performance.now() - startTime });
      return false;
    }
  };

  const changeEmail = async (lastPassword, newEmail) => {
    const user = getAuth().currentUser;
    try {
      // Reautentica sem fazer signOut — preserva a sessão em caso de erro
      const credential = EmailAuthProvider.credential(user.email, lastPassword);
      await reauthenticateWithCredential(user, credential);

      // Usa a rota admin (changeemail) que atualiza Auth + Firestore users + praticantes
      return await UsersService.updateEmail(user.uid, newEmail);
    } catch (error) {
      console.log("Erro ao alterar email:", error);
      return false;
    }
  };

  const changeName = async (lastPassword, newName) => {
    const user = getAuth().currentUser;
    try {
      await signOut(auth);
      const userCredential = await signInWithEmailAndPassword(auth, user.email, lastPassword);
      if (userCredential) {
        return await UsersService.updateName(user.uid, newName);
      }
      return false;
    } catch (error) {
      console.log("Erro ao alterar nome:", error);
      return false;
    }
  };

  const signup = async (userName, email, password) =>
    coalescerCadastro(`cadastro:${email}`, async () => {
    const startTime = performance.now();
    try {
      logAuthEvent({
        type: 'signup_attempt',
        email: email,
        userName: userName
      });
      
      const user = await createUserWithEmailAndPassword(auth, email, password);

      const newUser = {
        dadosPessoais: {
          name: userName,
        },
        dadosContato: {
          email: email,
          celular: "",
        },
        metadados: {
          ownerid: user.user.uid ? user.user.uid : "",
          owner: userName ? userName : "",
          creation: Timestamp.fromDate(new Date()),
          date: Timestamp.fromDate(new Date()),
          version: 1,
          active: true,
        },
        usersgroupsowned: [],
      };

      await UsersService.createWithID(user.user.uid, newUser);

      await updateProfile(user.user, {
        displayName: userName,
        photoURL: "",
      });

      // Verificação de email está desabilitada desde 10/10/2023

      logAuthEvent({
        type: 'signup_success',
        userId: user.user.uid,
        email: email,
        duration: performance.now() - startTime
      });

      return user;
    } catch (error) {
      console.log("Erro ao criar usuário:", error);
      logAuthEvent({
        type: 'signup_failed',
        email: email,
        error: error.message,
        duration: performance.now() - startTime
      });
      throw error;
    }
    });

  const signupWithoutLogoutCurrentUser = async (userName, email, password) =>
    coalescerCadastro(`cadastro:${email}`, async () => {
    let returnUser = undefined;
    return await createUserWithEmailAndPassword(secondaryAuth, email, password)
      .then(async (user) => {
        // //console.log("user=", user);
        returnUser = user;
        let newUser = {
          dadosPessoais: {
            name: userName,
          },
          dadosContato: {
            email: email,
            celular: "",
          },
          metadados: {
            ownerid: user.user.uid ? user.user.uid : "",
            owner: userName ? userName : "",
            creation: Timestamp.fromDate(new Date()),
            date: Timestamp.fromDate(new Date()),
            version: 1,
            active: true,
          },
          usersgroupsowned: [],
        };
        await UsersService.createWithID(user.user.uid, newUser);
        return await updateProfile(user.user, {
          displayName: userName,
          photoURL: "",
        })
          .then(async () => {
            const actionCodeSettings = {
              continueUrl: window.location.protocol + "//" + domainDetails.domain + "/signup-validation",
              url: window.location.protocol + "//" + domainDetails.domain + "/signup-validation",
              handleCodeInApp: false,
            };
            if (false) {
              //Desabilitado em 10/10/2023 por Fernando Villela para impedir envio de email de verificação, por enquanto.
              await sendEmailVerification(user.user, actionCodeSettings)
                .then(() => { })
                .catch((error) => {
                  console.log(error);
                });
            }
            return returnUser;
          })
          .catch((error) => {
            console.log(error);
          });
      })
      .catch((error) => {
        if (error.code === "auth/email-already-in-use") {
          //console.log(error);
          throw new Error("Este e-mail já está em uso!");
        } else {
          //console.log(error);
          throw new Error("Erro ao criar usuário!");
        }
      });
    });

  const login = async (email, password) => {
    const startTime = performance.now();
    try {
      logAuthEvent({
        type: 'login_attempt',
        email: email,
        isCpfLogin: isCpf(email)
      });
      
      // Buscar email por CPF se necessário.
      //
      // Isto roda ANTES do login, então não há token a enviar. Até 09/09/2026 o
      // passo era `POST /api/generic/getCollectionByConditions` em
      // `praticantes`, SEM autenticação — e a rota devolvia o cadastro inteiro
      // (CPF, nascimento, endereço, telefone) para quem soubesse um CPF, além
      // de aceitar qualquer outra condição em qualquer das cinco coleções.
      // Havia ainda um segundo caminho, um `fetch` de "compatibilidade" que
      // repetia a consulta sem token quando a primeira falhava.
      //
      // Agora o passo é `POST /api/usr/email-por-cpf`, que devolve SÓ o e-mail e
      // tem teto por IP. Sem fallback: se ele falhar, o login por CPF falha —
      // e é isso mesmo, porque o fallback era o buraco.
      if (isCpf(email)) {
        const cpf = email.replace(/\D/g, "");
        try {
          const { data } = await AuthenticatedApiService.post('/api/usr/email-por-cpf', { cpf });
          if (data && data.value && data.email) {
            email = data.email;
          } else {
            throw new Error("CPF não encontrado.");
          }
        } catch (apiError) {
          console.error("Falha ao resolver o e-mail pelo CPF:", apiError.message);
          throw new Error("CPF não encontrado ou inválido.");
        }
      }

      // Realizar login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Verificar email se necessário
      if (!userCredential.user.emailVerified) {
        reSendEmailVerification(userCredential);
      }

      // Verificar cadastros em paralelo para melhor performance
      const [userResult, praticanteResult] = await Promise.allSettled([
        UsersService.getOne(userCredential.user.uid),
        GenericService.getOne("praticantes", userCredential.user.uid)
      ]);

      const cadastroUsuarioCompleto = userResult.status === 'fulfilled' && userResult.value !== undefined;
      const cadastroPraticanteCompletoOK = praticanteResult.status === 'fulfilled' && praticanteResult.value !== undefined;
      
      const loginSuccess = cadastroUsuarioCompleto && cadastroPraticanteCompletoOK;
      
      // Se login foi bem sucedido, salvar timestamp para controle de sessão até meia-noite
      if (loginSuccess) {
        const loginTime = Date.now().toString();
        localStorage.setItem(loginTimeKey, loginTime);
      }
      
      logAuthEvent({
        type: 'login_success',
        userId: userCredential.user.uid,
        email: email,
        emailVerified: userCredential.user.emailVerified,
        cadastroCompleto: loginSuccess,
        duration: performance.now() - startTime
      });

      return loginSuccess;

    } catch (error) {
      console.error("Erro no login:", error);
      logAuthEvent({
        type: 'login_failed',
        email: email,
        error: error.message,
        errorCode: error.code,
        duration: performance.now() - startTime
      });
      throw error;
    }
  };

  const logout = async () => {
    const startTime = performance.now();
    try {
      logAuthEvent({ type: 'logout_attempt', userId: currentUser?.uid });
      
      await signOut(auth);
      
      // Limpar timestamp de login para controle de sessão
      localStorage.removeItem(loginTimeKey);
      
      // Limpar cache de urlDetails para que o próximo login recarregue
      // as configurações do portal corretamente
      resetUrlDetailsCache();
      
      logAuthEvent({ type: 'logout_success', userId: currentUser?.uid, duration: performance.now() - startTime });
      return true;
    } catch (error) {
      console.log("Erro ao fazer logout:", error);
      logAuthEvent({ type: 'logout_failed', userId: currentUser?.uid, error: error.message, duration: performance.now() - startTime });
      return false;
    }
  };

  const forgetPasswordSendEmail = (email) => {
    // continueUrl é o redirecionamento APÓS a redefinição ser concluída.
    // Para que o link do e-mail aponte diretamente para /reset-password
    // (evitando scanners de e-mail que consomem o oobCode ao pré-carregar
    // o link do Firebase), configure no Firebase Console:
    // Authentication → Templates → Password reset → Edit template →
    // "Customize action URL" → https://sg-cbtkd.web.app/reset-password
    //
    // IMPORTANTE: sempre usar o domínio base (sem subdomínio de federação)
    // no continueUrl. Subdomínios como fgtkd-rs.cbtkd.org.br não estão na
    // lista de domínios autorizados do Firebase e causam auth/unauthorized-continue-uri.
    // NÃO depender de domainDetails aqui, pois na página forgot-password o
    // usuário não está autenticado e o Firestore pode recusar a leitura,
    // deixando domainDetails null/undefined.
    const getBaseDomain = () => {
      if (domainDetails?.domain) return domainDetails.domain;
      // Extrair domínio base direto da URL removendo o subdomínio de federação
      const hostname = window.location.hostname;
      const knownRoots = ["preview-sg-cbtkd", "sg-cbtkd", "cbtkd", "sibrafe", "localhost"];
      const parts = hostname.split(".");
      if (parts.length <= 2 || knownRoots.includes(parts[0])) return hostname;
      return parts.slice(1).join(".");
    };
    const actionCodeSettings = {
      url: `${window.location.protocol}//${getBaseDomain()}/Login`,
    };
    return sendPasswordResetEmail(auth, email, actionCodeSettings);
  };

  const updateUserProfile = (auth, userDisplayName, userPhotoURL) => {
    return updateProfile(auth, {
      displayName: userDisplayName,
      photoURL: userPhotoURL,
    }).catch((error) => {
      //console.log(error);
    });
  };

  const reSendEmailVerification = async (auth) => {
    // Função simplificada - verificação de email está desabilitada desde 10/10/2023
    return true;

    /*
    const mauth = getAuth();
    const actionCodeSettings = {
      url: "https://sg-cbtkd.web.app/signup-validation",
      handleCodeInApp: false,
    };
    
    if (false) { // Desabilitado em 10/10/2023 por Fernando Villela
      try {
        await sendEmailVerification(mauth.currentUser, actionCodeSettings);
      } catch (error) {
        console.log("Erro ao enviar email de verificação:", error);
      }
    }
    return true;
    */
  };

  // const updateCelular = async (auth, userPhoneNumber) => {
  //   // 'recaptcha-container' is the ID of an element in the DOM.
  //   const applicationVerifier = new RecaptchaVerifier("recaptcha-container");
  //   const provider = new PhoneAuthProvider(auth);
  //   const verificationId = await provider.verifyPhoneNumber(
  //     userPhoneNumber,
  //     applicationVerifier
  //   );
  //   // Obtain the verificationCode from the user.
  //   const phoneCredential = PhoneAuthProvider.credential(
  //     verificationId,
  //     verificationCode
  //   );
  //   return await updatePhoneNumber(auth, phoneCredential).catch((error) => {
  //     //console.log(error);
  //   });

  //   // return updatePhoneNumber(auth, {
  //   //   displayName: userDisplayName,
  //   //   photoURL: userPhotoURL,
  //   // }).catch((error) => {
  //   //   //console.log(error);
  //   // });
  // };

  // Função para obter o usuário Firebase original com getIdToken
  const getFirebaseUser = () => {
    return auth.currentUser;
  };

  // ✅ CORREÇÃO REACT 19: Estabilizar currentUser com useMemo
  // Previne loops infinitos causados por React 19 automatic batching
  // rulesKey é uma string derivada do conteúdo de rulesOwned — muda quando
  // o array muda de conteúdo, mesmo que o tamanho permaneça igual.
  const rulesKey = currentUser?.rulesOwned?.join(',') ?? '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentUserStable = useMemo(() => currentUser, [currentUser, rulesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect para autenticação cross-subdomain via custom token na URL
  // Ex: fctkd.cbtkd.org.br?_cb=<timestamp>&sessionToken=<token>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('sessionToken');
    if (!sessionToken) return;

    // Remove o token e o parâmetro de cache-bust da URL imediatamente (segurança + limpeza)
    params.delete('sessionToken');
    params.delete('_cb');
    const newSearch = params.toString();
    const cleanUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);

    // Faz login com o custom token — onAuthStateChanged cuida do resto
    signInWithCustomToken(auth, sessionToken).catch((error) => {
      console.error('[AUTH] Falha ao autenticar com sessionToken:', error.message);
    });
  }, []);

  /**
   * Navega para um portal diferente (outro subdomínio) preservando o login.
   * Gera um custom token via backend e o anexa na URL de destino.
   * O portal destino consome o token automaticamente ao carregar.
   *
   * O parâmetro _cb (cache-bust) é adicionado com timestamp para garantir que
   * o browser/CDN sempre busque um index.html fresco, evitando erro de MIME type
   * causado por chunks JS obsoletos referenciados por um index.html em cache.
   */
  const navigateToPortal = async (portalUrl, newTab = false) => {
    try {
      const user = getAuth().currentUser;
      if (!user) {
        if (newTab) window.open(portalUrl, '_blank', 'noreferrer');
        else window.location.href = portalUrl;
        return;
      }
      // Abrir janela ANTES do await para não ser bloqueado pelo popup blocker do navegador
      const newWindow = newTab ? window.open('', '_blank') : null;
      const response = await UsersService.getSessionToken();
      const separator = portalUrl.includes('?') ? '&' : '?';
      // _cb: cache-bust timestamp — garante URL única em cada navegação,
      // impedindo que o browser/CDN use um index.html cacheado com hashes antigos.
      const cacheBust = `_cb=${Date.now()}`;
      const targetUrl = response?.data?.token
        ? `${portalUrl}${separator}${cacheBust}&sessionToken=${encodeURIComponent(response.data.token)}`
        : `${portalUrl}${separator}${cacheBust}`;
      if (newWindow) {
        newWindow.location.href = targetUrl;
      } else {
        window.location.href = targetUrl;
      }
    } catch (error) {
      console.error('[AUTH] Erro ao gerar sessionToken para navegação:', error.message);
      if (newTab) window.open(portalUrl, '_blank', 'noreferrer');
      else window.location.href = portalUrl;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser: currentUserStable, // ✅ Usar versão estável
        domainDetails,
        loading, // ✅ Exportar estado de loading para componentes
        sessionWarning, // ✅ Aviso de sessão próxima de expirar (passa meia-noite)
        federacaoMismatch, // ✅ Aviso de portal de federação não filiada
        changePortal,
        signup,
        signupWithoutLogoutCurrentUser,
        login,
        logout,
        existEmail,
        forgetPasswordSendEmail,
        updateUserProfile,
        reSendEmailVerification,
        changePassword,
        changeEmail,
        changeName,
        getCachedUrlDetails, // Adicionando função que estava faltando
        resetUrlDetailsCache, // Para debug/troubleshooting
        getFirebaseUser, // Novo método para acessar usuário Firebase original
        navigateToPortal, // ✅ Navegação cross-subdomínio com login persistente
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const AuthContextProvider = memo(AuthContextProviderComponent);
